import { DeliveryType, PaymentType } from '@analytics/const';
import { CheckoutAnalyticsGaV4Service, CheckoutAnalyticsService, RetailRocketService } from '@analytics/feature';
import { Injectable } from '@angular/core';
import { ActiveCartService } from '@cart/data-access';
import { CheckoutActions as CustomCheckoutActions } from '@checkout/data-access';
import { isCheckoutRoute } from '@checkout/utils';
import { CityService } from '@city/feature';
import { EnvironmentService } from '@core/feature';
import { Actions, EffectNotification, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { CustomOrder } from '@order/const';
import { PaymentModeCodes } from '@payment/const';
import { OrderEntry } from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user';
import { User } from '@user/const';
import { EMPTY, Observable, iif } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { CheckoutContactFormService } from '../services';

const paymentTypes = {
  [PaymentModeCodes.Sbrf]: PaymentType.Sbrf,
  [PaymentModeCodes.CardDelivery]: PaymentType.CardDelivery,
  [PaymentModeCodes.CashDelivery]: PaymentType.CashDelivery,
  [PaymentModeCodes.CashCardShop]: PaymentType.CashCardShop,
  [PaymentModeCodes.Sbp]: PaymentType.Sbp,
};

const retrivalMethodsMap = {
  delivery: DeliveryType.Delivery,
  pickup: DeliveryType.Pickup,
};

@Injectable()
export class CheckoutAnalyticsEffects {
  goToCheckoutPage$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ROUTER_NAVIGATED),
        filter(
          ({
            payload: {
              routerState: { semanticRoute },
            },
          }) => isCheckoutRoute(semanticRoute),
        ),
        map(() => {
          this.activeCartService
            .getActive()
            .pipe(
              take(1),
              tap(({ entries }) => {
                this.checkoutAnalyticsGaV4Service.onGoToCheckoutPage(entries);
              }),
            )
            .subscribe();
        }),
      ),
    { dispatch: false },
  );

  setRetrivalMethod$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CustomCheckoutActions.SET_ACTIVE_RECEIPT_TYPE),
        filter((action: CustomCheckoutActions.SetReceiptType) => Boolean(action.payload)),
        map((action: CustomCheckoutActions.SetReceiptType) => action.payload),
        distinctUntilChanged(),
        switchMap((retrivalMethod) =>
          this.activeCartService.getEntries().pipe(
            map((entries) => [retrivalMethod, entries]),
            take(1),
          ),
        ),
        map(([retrivalMethod, entries]: [string, OrderEntry[]]) => {
          this.checkoutAnalyticsGaV4Service.onSetRetrievalMethod(
            entries,
            retrivalMethodsMap[retrivalMethod as keyof typeof retrivalMethodsMap],
          );
        }),
      ),
    { dispatch: false },
  );

  setPaymentV4$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CustomCheckoutActions.SET_PAYMENT),
        map((action: CustomCheckoutActions.SetPayment) => action.payload),
        distinctUntilChanged(),
        withLatestFrom(this.activeCartService.getEntries()),
        map(([payload, entries]: [string, OrderEntry[]]) => {
          const paymentType = paymentTypes[payload as keyof typeof paymentTypes];
          this.checkoutAnalyticsGaV4Service.onSetPayment(entries, paymentType);
        }),
      ),
    { dispatch: false },
  );

  setPaymentBindingV4$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CustomCheckoutActions.INIT_CARD_BINDING),
        withLatestFrom(this.activeCartService.getEntries()),
        map(([, entries]: [string, OrderEntry[]]) => {
          this.checkoutAnalyticsGaV4Service.onSetPayment(entries, 'Добавить карту');
        }),
      ),
    { dispatch: false },
  );

  confirmOrder$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CustomCheckoutActions.SET_ORDER_DETAILS),
        map(({ payload }) => payload),
        withLatestFrom(
          this.cityService.activeBaseStoreCode$,
          this.checkoutContactFormService.submitEvent.pipe(map(() => this.checkoutContactFormService.queryData.email)),
          this.userAccountService.get().pipe(filter<User>(Boolean)),
        ),
        map(([order, activeBaseStoreCode, orderEmail, user]: [CustomOrder, string, string, User]) => {
          if (order.code) {
            this.checkoutAnalyticsService.onConfirmOrder(order);
            this.retailRocketService.onConfirmOrder({ order, activeBaseStoreCode, orderEmail, user });
          }
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private activeCartService: ActiveCartService,
    private checkoutAnalyticsGaV4Service: CheckoutAnalyticsGaV4Service,
    private retailRocketService: RetailRocketService,
    private cityService: CityService,
    private checkoutContactFormService: CheckoutContactFormService,
    private environmentService: EnvironmentService,
    private userAccountService: UserAccountFacade,
    private checkoutAnalyticsService: CheckoutAnalyticsService,
  ) {}

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>): Observable<EffectNotification> {
    return iif(() => this.environmentService.isPlatformBrowser, resolvedEffects$, EMPTY);
  }
}
